export type PdfVisualSignaturePlacement = {
  id: string;
  sourceId: string;
  pageIndex: number;
  imageUrl: string;
  rect: { x: number; y: number; width: number; height: number };
  rotation?: number;
};

type PdfVisualSignatureImage = {
  bytes: Uint8Array;
  mimeType: 'image/png' | 'image/jpeg';
};

type PdfVisualSignatureImageResolver = (
  imageUrl: string
) => Promise<PdfVisualSignatureImage>;

export type PdfVisualSignatureDrawOptions = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
};

export function getPdfVisualSignatureDrawOptions({
  page,
  image,
  placement,
}: {
  page: { width: number; height: number };
  image: { width: number; height: number };
  placement: PdfVisualSignaturePlacement;
}): PdfVisualSignatureDrawOptions {
  const box = {
    x: placement.rect.x * page.width,
    y: page.height - (placement.rect.y + placement.rect.height) * page.height,
    width: placement.rect.width * page.width,
    height: placement.rect.height * page.height,
  };
  const scale = Math.min(
    box.width / Math.max(1, image.width),
    box.height / Math.max(1, image.height)
  );
  const width = image.width * scale;
  const height = image.height * scale;
  const centerX = box.x + box.width / 2;
  const centerY = box.y + box.height / 2;
  // CSS/canvas rotations are clockwise in screen coordinates. PDF rotations
  // are counter-clockwise in bottom-left coordinates, hence the sign flip.
  const rotation = placement.rotation ? -placement.rotation : 0;
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return {
    x: centerX - (cos * width) / 2 + (sin * height) / 2,
    y: centerY - (sin * width) / 2 - (cos * height) / 2,
    width,
    height,
    rotation,
  };
}

export async function buildPdfWithVisualSignatures({
  sourcePdf,
  signaturePlacements,
  resolveImage = resolvePdfVisualSignatureImage,
}: {
  sourcePdf: Blob | ArrayBuffer | Uint8Array;
  signaturePlacements: PdfVisualSignaturePlacement[];
  resolveImage?: PdfVisualSignatureImageResolver;
}) {
  const { degrees, PDFDocument } = await import('pdf-lib');
  const sourceBytes =
    sourcePdf instanceof Blob
      ? new Uint8Array(await sourcePdf.arrayBuffer())
      : sourcePdf instanceof ArrayBuffer
        ? new Uint8Array(sourcePdf)
        : sourcePdf;
  const pdfDoc = await PDFDocument.load(sourceBytes);
  const pages = pdfDoc.getPages();
  const embeddedImages = new Map<
    string,
    Awaited<ReturnType<typeof pdfDoc.embedPng>>
  >();

  for (const placement of signaturePlacements) {
    const page = pages[placement.pageIndex];
    if (!page) continue;
    let image = embeddedImages.get(placement.imageUrl);
    if (!image) {
      const resolved = await resolveImage(placement.imageUrl);
      image =
        resolved.mimeType === 'image/png'
          ? await pdfDoc.embedPng(resolved.bytes)
          : await pdfDoc.embedJpg(resolved.bytes);
      embeddedImages.set(placement.imageUrl, image);
    }
    const size = page.getSize();
    const draw = getPdfVisualSignatureDrawOptions({
      page: size,
      image,
      placement,
    });
    page.drawImage(image, {
      x: draw.x,
      y: draw.y,
      width: draw.width,
      height: draw.height,
      rotate: degrees(draw.rotation),
    });
  }

  return pdfDoc.save();
}

async function resolvePdfVisualSignatureImage(
  imageUrl: string
): Promise<PdfVisualSignatureImage> {
  const response = await fetch(imageUrl, { credentials: 'include' });
  if (!response.ok) {
    throw new Error('Could not load PDF signature image');
  }
  const bytes = new Uint8Array(await response.arrayBuffer());
  const contentType = response.headers.get('content-type')?.split(';')[0];
  const mimeType = detectSignatureImageMimeType(bytes, contentType);
  if (!mimeType) {
    throw new Error('PDF signature image must be PNG or JPEG');
  }
  return { bytes, mimeType };
}

function detectSignatureImageMimeType(
  bytes: Uint8Array,
  contentType?: string
): PdfVisualSignatureImage['mimeType'] | null {
  if (contentType === 'image/png' || contentType === 'image/jpeg') {
    return contentType;
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'image/png';
  }
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    return 'image/jpeg';
  }
  return null;
}
